import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  StyleSheet,
  PermissionsAndroid,
  Platform,
  Modal,
  Linking,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useUnifiedSearchQuery } from '../store/api/UiApi';
import SearchIcon from './Icons/SearchIcon';
import CloseIcon from './Icons/CloseIcon';
import MapPinIcon from './Icons/MapPinIcon';
import StarIcon from './Icons/StarIcon';
import LocationIcon from './Icons/LocationIcon';
import TimeIcon from './Icons/TimeIcon';
import DirectionsIcon from './Icons/DirectionsIcon';
import SearchableCategoryDropdown from './SearchableCategoryDropdown';
import { getFontSize, getResponsiveValue } from '../utils';

const DiscoverSection = ({ navigation, theme }) => {
  const { t } = useTranslation();

  // Accordion state
  const [isExpanded, setIsExpanded] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [focusedInput, setFocusedInput] = useState(null);

  // Search results state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchParams, setSearchParams] = useState(null);

  // Suggestions state
  const [suggestParams, setSuggestParams] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suppressSuggest, setSuppressSuggest] = useState(false);

  // Location state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLocationSuggesting, setIsLocationSuggesting] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // API queries
  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useUnifiedSearchQuery(searchParams, {
    skip: !searchParams,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: suggestData,
    isLoading: isSuggestLoading,
    error: suggestError,
    refetch: refetchSuggest,
  } = useUnifiedSearchQuery(suggestParams, {
    skip: !suggestParams,
    refetchOnMountOrArgChange: true,
  });

  // Handle search results from API
  useEffect(() => {
    if (searchData) {
      if (searchData.items && Array.isArray(searchData.items)) {
        setSearchResults(searchData.items);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
      }
    }
  }, [searchData]);

  // Handle search suggestions from API
  useEffect(() => {
    if (suggestData && suggestData.items) {
      setSuggestions(
        suggestData.items
          .map((item) => ({
            label: item.name,
            value: item.id,
          }))
          .filter((s) => s.label)
      );
    }
  }, [suggestData]);

  const handleSearch = async () => {
    if (!searchQuery.trim() && !selectedLocation.trim()) {
      Alert.alert(
        'Search Required',
        'Please enter a business name or location to search.'
      );
      return;
    }

    setIsSearching(true);
    setShowResultsModal(true);

    // Clear location suggestions when search is performed
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);

    // Close all other dropdowns
    setShowSuggestions(false);
    setFocusedInput(null);

    try {
      // Build search parameters for the unified search API
      const newSearchParams = {
        keywords: searchQuery.trim() || undefined,
        location: selectedLocation.trim() || undefined,
        size: 20,
      };

      // Add user coordinates if available
      if (userLocation) {
        newSearchParams.latitude = userLocation.latitude;
        newSearchParams.longitude = userLocation.longitude;
      }

      // Add category filter if selected
      if (
        selectedCategory &&
        typeof selectedCategory === 'object' &&
        selectedCategory.name
      ) {
        newSearchParams.category = selectedCategory.name;
      }

      const hasChanged =
        JSON.stringify(newSearchParams) !== JSON.stringify(searchParams);

      if (hasChanged) {
        setSearchResults([]);
        setSearchParams(newSearchParams);
      } else {
        if (searchResults.length === 0) {
          refetchSearch();
        } else {
          setShowResultsModal(true);
        }
      }
    } catch (error) {
      Alert.alert(
        'Search Error',
        'An error occurred while searching. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchFocus = () => {
    setFocusedInput('search');
    setShowLocationSuggestions(false);
    setShowSearchResults(false);
    setShowSearchModal(true);
    setShowSuggestions(!suppressSuggest && Boolean((searchQuery || '').trim()));
  };

  const handleLocationFocus = () => {
    setFocusedInput('location');
    setShowSuggestions(false);
    setShowSearchResults(false);
    setShowLocationModal(true);
  };

  const closeAllDropdowns = () => {
    setShowSuggestions(false);
    setShowLocationSuggestions(false);
    setShowSearchResults(false);
    setFocusedInput(null);
  };

  const handleSearchChange = (text) => {
    setSuppressSuggest(false);
    setSearchQuery(text);
    setDebouncedSearchQuery(text);

    if (!text.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchParams(null);
      setSuggestParams(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleLocationChange = (text) => {
    setSelectedLocation(text);

    if (!text.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchParams(null);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const fetchLocationSuggestions = useCallback(async (query) => {
    try {
      setIsLocationSuggesting(true);
      const searchUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=8&addressdetails=1`;

      const response = await fetch(searchUrl);
      const data = await response.json();

      if (Array.isArray(data)) {
        const suggestions = data
          .map((item) => {
            const address = item.address;
            const display =
              `${address?.city || address?.town || address?.village || ''}, ${address?.state || ''}`.trim();
            const fullAddress = item.display_name;

            return {
              display,
              fullAddress,
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
            };
          })
          .filter((item) => item.display);

        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(true);
      }
    } catch (error) {
      console.error('Location search error:', error);
    } finally {
      setIsLocationSuggesting(false);
    }
  }, []);

  // Debounced search suggestions
  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        // Set up suggestions query - same payload as splash screen
        const suggestPayload = {
          keywords: debouncedSearchQuery.trim(),
          size: 6,
        };

        // Add location if available
        if (selectedLocation && selectedLocation.trim()) {
          suggestPayload.location = selectedLocation.trim();
        }

        setSuggestParams(suggestPayload);
      }, 300); // 300ms debounce delay
      return () => clearTimeout(timeoutId);
    } else {
      setSuggestParams(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [debouncedSearchQuery, selectedLocation]);

  // Debounced location search
  useEffect(() => {
    if (selectedLocation && selectedLocation.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        fetchLocationSuggestions(selectedLocation.trim());
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  }, [selectedLocation, fetchLocationSuggestions]);

  // Auto-fill location on component mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  const requestLocationPermission = async () => {
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
          {
            title: 'Location Permission',
            message:
              'Waitkyu needs access to your location to find nearby businesses.',
            buttonNeutral: 'Ask Me Later',
            buttonNegative: 'Cancel',
            buttonPositive: 'OK',
          }
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      } catch (err) {
        console.warn('Location permission error:', err);
        return false;
      }
    }
    return true; // iOS handles permissions differently
  };

  const getCurrentLocation = async () => {
    try {
      setIsLocationLoading(true);

      // Check if we already have location permission
      const hasPermission = await requestLocationPermission();

      if (!hasPermission) {
        setIsLocationLoading(false);
        return;
      }

      // Get current position
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Reverse geocode to get address
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );
            const data = await response.json();

            if (data && data.address) {
              const address = data.address;
              const city =
                address.city || address.town || address.village || '';
              const state = address.state || '';
              const locationString = `${city}, ${state}`.trim();

              if (locationString) {
                setSelectedLocation(locationString);
                setUserLocation({ latitude, longitude });
              }
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }

          setIsLocationLoading(false);
        },
        (error) => {
          console.error('Location error:', error);
          setIsLocationLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
        }
      );
    } catch (error) {
      console.error('Location permission error:', error);
      setIsLocationLoading(false);
    }
  };

  const handleBusinessClick = (business) => {
    if (business.type === 'queue') {
      // Queue type - navigate directly to JoinQueueScreen
      navigation.navigate('JoinQueueScreen', {
        qid: business.id,
      });
    } else if (!business.fromInternet) {
      // Business is on Waitkyu - navigate to business queues
      navigation.navigate('BusinessQueuesScreen', {
        business_id: business.parent_business_id,
        businessName: business.name,
      });
    } else {
      // External business - navigate to ExternalBusinessDetailScreen
      navigation.navigate('ExternalBusinessDetailScreen', {
        itemId: business.id,
        fromDashboard: true,
      });
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchQuery(suggestion.label);
    setSuggestParams(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowSearchModal(false);
    setFocusedInput(null);
  };

  const closeSearchModal = () => {
    setShowSearchModal(false);
    setShowSuggestions(false);
    setFocusedInput(null);
  };

  const closeLocationModal = () => {
    setShowLocationModal(false);
    setShowLocationSuggestions(false);
    setFocusedInput(null);
  };

  const closeResultsModal = () => {
    setShowResultsModal(false);
    setShowSearchResults(false);
  };

  const handleLocationSuggestionSelect = (suggestion) => {
    setSelectedLocation(suggestion.display);
    setUserLocation({
      latitude: suggestion.lat,
      longitude: suggestion.lon,
    });
    setShowLocationModal(false);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    setFocusedInput(null);
  };

  const openInMaps = (latitude, longitude, businessName) => {
    try {
      // For web, open in Google Maps
      if (Platform.OS === 'web') {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
        window.open(url, '_blank');
      } else {
        // For mobile, try to open in native maps app
        const url =
          Platform.OS === 'ios'
            ? `http://maps.apple.com/?daddr=${latitude},${longitude}&dirflg=d`
            : `google.navigation:q=${latitude},${longitude}`;

        Linking.openURL(url).catch(() => {
          // Fallback to Google Maps web
          const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
          Linking.openURL(fallbackUrl);
        });
      }
    } catch (error) {
      console.error('Error opening maps:', error);
    }
  };

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const styles = StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      marginBottom: 16,
    },

    title: {
      fontSize: getFontSize(20, 18, 16),
      fontFamily: 'Poppins',
      fontWeight: '600',
      color: theme.colors.onSurface,
      marginBottom: 4,
    },
    subtitle: {
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    searchContainer: {
      marginBottom: 16,
    },
    searchInputRow: {
      flexDirection: 'column',
      gap: 12,
      marginBottom: 16,
    },
    searchInputWrapper: {
      width: '100%',
      position: 'relative',
    },
    searchInput: {
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      color: theme.colors.onSurface,
    },
    clearInputButton: {
      position: 'absolute',
      right: 12,
      top: 12,
      zIndex: 10,
    },
    locationInputWrapper: {
      width: '100%',
      position: 'relative',
    },
    locationInput: {
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      color: theme.colors.onSurface,
    },
    clearLocationButton: {
      position: 'absolute',
      right: 12,
      top: 12,
      zIndex: 10,
    },
    locationButton: {
      position: 'absolute',
      right: 12,
      top: 12,
      zIndex: 10,
      padding: 4,
    },
    locationLoadingIndicator: {
      position: 'absolute',
      right: 12,
      top: 12,
      zIndex: 10,
    },
    categoryDropdown: {
      width: '100%',
    },
    searchButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 16,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      width: '100%',
    },
    searchButtonText: {
      color: theme.colors.onPrimary,
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '600',
    },
    suggestionsDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: theme.colors.surface,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.outline,
      zIndex: 1000,
      maxHeight: 200,
    },
    suggestionItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.outline,
    },
    suggestionText: {
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      color: theme.colors.onSurface,
    },

    searchResultsContainer: {
      marginTop: 16,
    },
    searchResultsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    searchResultsCount: {
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    closeResultsButton: {
      padding: 4,
    },
    closeResultsText: {
      fontSize: getFontSize(16, 14, 12),
      color: theme.colors.onSurfaceVariant,
    },
    businessCard: {
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 8,
      padding: 12,
      marginBottom: 8,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 8,
    },
    businessNameSection: {
      flex: 1,
    },
    businessName: {
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '600',
      color: theme.colors.onSurface,
      marginBottom: 2,
    },
    parentBusinessName: {
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      fontWeight: '500',
      color: theme.colors.onSurfaceVariant,
      marginBottom: 4,
    },
    ratingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    ratingsText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    categorySection: {
      alignItems: 'flex-end',
    },
    categoryText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.primary,
      backgroundColor: theme.colors.primaryContainer,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 4,
    },
    statusSection: {
      marginBottom: 8,
    },
    statusText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      fontWeight: '600',
    },
    addressSection: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
    },
    addressContainer: {
      flex: 1,
    },
    addressText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    loadingContainer: {
      alignItems: 'center',
      padding: 20,
    },
    loadingText: {
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
      marginTop: 8,
    },
    // Modal styles
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContainer: {
      backgroundColor: theme.colors.surface,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      paddingTop: 20,
      paddingHorizontal: 20,
      paddingBottom: 40,
      maxHeight: '80%',
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    modalTitle: {
      fontSize: getFontSize(18, 16, 14),
      fontFamily: 'Poppins',
      fontWeight: '600',
      color: theme.colors.onSurface,
    },
    closeModalButton: {
      padding: 8,
    },
    modalSearchInput: {
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      color: theme.colors.onSurface,
      marginBottom: 16,
    },
    currentLocationButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginBottom: 16,
      opacity: 1,
    },
    currentLocationIcon: {
      marginRight: 12,
    },
    currentLocationText: {
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '600',
      color: '#FFFFFF',
    },
    modalSuggestionsList: {
      maxHeight: 400,
    },
    modalSuggestionItem: {
      padding: 16,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.outline,
      flexDirection: 'row',
      alignItems: 'center',
    },
    modalSuggestionIcon: {
      marginRight: 12,
    },
    modalSuggestionContent: {
      flex: 1,
    },
    modalSuggestionName: {
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '500',
      color: theme.colors.onSurface,
      marginBottom: 4,
    },
    modalSuggestionCategory: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    modalSuggestionAddress: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
      marginTop: 2,
    },
    noSuggestionsText: {
      textAlign: 'center',
      fontSize: getFontSize(14, 12, 10),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
      padding: 20,
    },
    // Enhanced business card styles (matching splash screen)
    businessCard: {
      backgroundColor: theme.colors.surfaceVariant,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.outline,
      padding: 12,
      marginBottom: 12,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 8,
      paddingBottom: 6,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.outline,
    },
    businessNameSection: {
      flex: 1,
      marginRight: 8,
    },
    businessName: {
      fontSize: getFontSize(16, 14, 12),
      fontFamily: 'Poppins',
      fontWeight: '600',
      color: theme.colors.onSurface,
      marginBottom: 4,
    },
    ratingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    ratingsText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    categorySection: {
      alignItems: 'flex-end',
    },
    categoryText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.primary,
      backgroundColor: theme.colors.primaryContainer,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 4,
    },
    statusSection: {
      marginBottom: 8,
    },
    statusText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      fontWeight: '600',
    },
    addressSection: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
      marginBottom: 8,
    },
    addressContainer: {
      flex: 1,
    },
    addressText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    operatingHoursSection: {
      marginBottom: 6,
    },
    operatingHoursText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    waitTimeSection: {
      marginBottom: 6,
    },
    waitTimeText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    distanceSection: {
      marginBottom: 6,
    },
    distanceText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onSurfaceVariant,
    },
    cardFooter: {
      marginTop: 6,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: theme.colors.outline,
    },
    footerContent: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    leftSection: {
      flex: 0,
    },
    rightSection: {
      flex: 0,
      alignItems: 'flex-end',
    },
    platformBadge: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      alignSelf: 'flex-start',
      minWidth: 'auto',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.2,
      shadowRadius: 2,
      elevation: 2,
    },
    platformText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onPrimary,
      fontWeight: '600',
      textAlign: 'center',
      flexWrap: 'nowrap',
      numberOfLines: 1,
    },
    directionsButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 6,
      gap: 4,
    },
    directionsText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      color: theme.colors.onPrimary,
      fontWeight: '600',
    },
    // Accordion styles
    accordionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 16,
      paddingHorizontal: 16,
    },
    accordionHeaderContent: {
      flex: 1,
    },
    accordionIcon: {
      padding: 8,
    },
    expandedContent: {
      paddingHorizontal: 16,
      paddingBottom: 16,
    },
  });

  return (
    <View style={styles.container}>
      {/* Accordion Header */}
      <TouchableOpacity
        style={styles.accordionHeader}
        onPress={toggleAccordion}
      >
        <View style={styles.accordionHeaderContent}>
          <Text style={styles.title}>Discover Amazing Businesses</Text>
          <Text style={styles.subtitle}>
            Find and join queues at your favorite places instantly
          </Text>
        </View>
        <View style={styles.accordionIcon}>
          {isExpanded ? (
            <CloseIcon width={24} height={24} color={theme.colors.onSurface} />
          ) : (
            <SearchIcon width={24} height={24} color={theme.colors.onSurface} />
          )}
        </View>
      </TouchableOpacity>

      {/* Expanded Content */}
      {isExpanded && (
        <View style={styles.expandedContent}>
          <View style={styles.searchContainer}>
            <View style={styles.searchInputRow}>
              <View style={styles.searchInputWrapper}>
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search for businesses, services..."
                  placeholderTextColor={theme.colors.onSurfaceVariant}
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSuggestions(false);
                    }, 300);
                  }}
                />
                {searchQuery?.length > 0 && (
                  <TouchableOpacity
                    onPress={() => handleSearchChange('')}
                    style={styles.clearInputButton}
                    accessibilityLabel="Clear search"
                  >
                    <CloseIcon
                      width={18}
                      height={18}
                      color={theme.colors.onSurfaceVariant}
                    />
                  </TouchableOpacity>
                )}
              </View>

              {/* Search Suggestions Dropdown */}
              {showSuggestions &&
                suggestions.length > 0 &&
                !showSearchResults && (
                  <View style={styles.suggestionsDropdown}>
                    {suggestions.map((s, idx) => (
                      <View
                        key={`${s.label}-${idx}`}
                        style={styles.suggestionItem}
                        onTouchStart={() => {
                          setSuppressSuggest(true);
                          setSearchQuery(s.label);
                          setSuggestParams(null);
                          setSuggestions([]);
                          setShowSuggestions(false);
                          setFocusedInput(null);
                        }}
                      >
                        <Text style={styles.suggestionText}>{s.label}</Text>
                      </View>
                    ))}
                  </View>
                )}

              <View style={styles.locationInputWrapper}>
                <TextInput
                  style={styles.locationInput}
                  placeholder={
                    isLocationLoading ? 'Getting your location...' : 'Location'
                  }
                  placeholderTextColor={theme.colors.onSurfaceVariant}
                  value={selectedLocation}
                  onChangeText={handleLocationChange}
                  onFocus={handleLocationFocus}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowLocationSuggestions(false);
                    }, 300);
                  }}
                  editable={!isLocationLoading}
                />
                {isLocationLoading && (
                  <View style={styles.locationLoadingIndicator}>
                    <ActivityIndicator
                      size="small"
                      color={theme.colors.primary}
                    />
                  </View>
                )}
                {!isLocationLoading && !selectedLocation && (
                  <TouchableOpacity
                    onPress={getCurrentLocation}
                    style={styles.locationButton}
                    accessibilityLabel="Get current location"
                  >
                    <MapPinIcon
                      width={18}
                      height={18}
                      color={theme.colors.primary}
                    />
                  </TouchableOpacity>
                )}
                {selectedLocation && !isLocationLoading && (
                  <TouchableOpacity
                    onPress={() => {
                      setSelectedLocation('');
                      setUserLocation(null);
                      setLocationSuggestions([]);
                      setShowLocationSuggestions(false);
                    }}
                    style={styles.clearLocationButton}
                    accessibilityLabel="Clear location"
                  >
                    <CloseIcon
                      width={18}
                      height={18}
                      color={theme.colors.onSurfaceVariant}
                    />
                  </TouchableOpacity>
                )}
              </View>

              <SearchableCategoryDropdown
                value={
                  typeof selectedCategory === 'object'
                    ? selectedCategory.id
                    : selectedCategory
                }
                onSelect={(category) => {
                  if (!category) {
                    setSelectedCategory('');
                    return;
                  }
                  setSelectedCategory(category);
                  closeAllDropdowns();
                  setFocusedInput(null);
                }}
                placeholder="Select a category"
                style={styles.categoryDropdown}
                colorScheme="default"
              />
            </View>

            <TouchableOpacity
              style={styles.searchButton}
              onPress={handleSearch}
            >
              {isSearching ? (
                <View style={styles.searchButtonLoading}>
                  <Text style={styles.searchButtonText}>Searching...</Text>
                </View>
              ) : (
                <>
                  <SearchIcon
                    width={20}
                    height={20}
                    color={theme.colors.onPrimary}
                  />
                  <Text style={styles.searchButtonText}>Search</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Search Modal */}
      <Modal
        visible={showSearchModal}
        animationType="slide"
        transparent={true}
        onRequestClose={closeSearchModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Search Businesses</Text>
              <TouchableOpacity
                onPress={closeSearchModal}
                style={styles.closeModalButton}
              >
                <CloseIcon
                  width={24}
                  height={24}
                  color={theme.colors.onSurface}
                />
              </TouchableOpacity>
            </View>

            <TextInput
              style={styles.modalSearchInput}
              placeholder="Search for businesses, services..."
              placeholderTextColor={theme.colors.onSurfaceVariant}
              value={searchQuery}
              onChangeText={handleSearchChange}
              autoFocus={true}
            />

            <ScrollView style={styles.modalSuggestionsList}>
              {isSuggestLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator
                    size="large"
                    color={theme.colors.primary}
                  />
                  <Text style={styles.loadingText}>Searching...</Text>
                </View>
              ) : suggestions.length > 0 || searchQuery.trim() ? (
                <>
                  {/* Custom user input as first option */}
                  {searchQuery.trim() && (
                    <TouchableOpacity
                      key="custom-search"
                      style={styles.modalSuggestionItem}
                      onPress={() =>
                        handleSuggestionSelect({ label: searchQuery.trim() })
                      }
                    >
                      <View style={styles.modalSuggestionIcon}>
                        <SearchIcon
                          width={20}
                          height={20}
                          color={theme.colors.onSurfaceVariant}
                        />
                      </View>
                      <View style={styles.modalSuggestionContent}>
                        <Text style={styles.modalSuggestionName}>
                          "{searchQuery.trim()}"
                        </Text>
                        <Text style={styles.modalSuggestionCategory}>
                          Search for this
                        </Text>
                      </View>
                    </TouchableOpacity>
                  )}
                  {/* API suggestions */}
                  {suggestions.map((suggestion, idx) => (
                    <TouchableOpacity
                      key={`${suggestion.label}-${idx}`}
                      style={styles.modalSuggestionItem}
                      onPress={() => handleSuggestionSelect(suggestion)}
                    >
                      <View style={styles.modalSuggestionIcon}>
                        <SearchIcon
                          width={20}
                          height={20}
                          color={theme.colors.onSurfaceVariant}
                        />
                      </View>
                      <View style={styles.modalSuggestionContent}>
                        <Text style={styles.modalSuggestionName}>
                          {suggestion.label}
                        </Text>
                        <Text style={styles.modalSuggestionCategory}>
                          Business
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </>
              ) : searchQuery.trim() ? (
                <Text style={styles.noSuggestionsText}>
                  No businesses found for "{searchQuery}"
                </Text>
              ) : (
                <Text style={styles.noSuggestionsText}>
                  Start typing to search for businesses
                </Text>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Location Modal */}
      <Modal
        visible={showLocationModal}
        animationType="slide"
        transparent={true}
        onRequestClose={closeLocationModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Location</Text>
              <TouchableOpacity
                onPress={closeLocationModal}
                style={styles.closeModalButton}
              >
                <CloseIcon
                  width={24}
                  height={24}
                  color={theme.colors.onSurface}
                />
              </TouchableOpacity>
            </View>

            <TextInput
              style={styles.modalSearchInput}
              placeholder="Search for location..."
              placeholderTextColor={theme.colors.onSurfaceVariant}
              value={selectedLocation}
              onChangeText={handleLocationChange}
              autoFocus={true}
            />

            {/* Current Location Button */}
            <TouchableOpacity
              style={styles.currentLocationButton}
              onPress={getCurrentLocation}
              disabled={isLocationLoading}
            >
              <View style={styles.currentLocationIcon}>
                {isLocationLoading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <MapPinIcon width={20} height={20} color="#FFFFFF" />
                )}
              </View>
              <Text style={styles.currentLocationText}>
                {isLocationLoading
                  ? 'Getting location...'
                  : 'Use Current Location'}
              </Text>
            </TouchableOpacity>

            <ScrollView style={styles.modalSuggestionsList}>
              {isLocationSuggesting ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator
                    size="large"
                    color={theme.colors.primary}
                  />
                  <Text style={styles.loadingText}>Searching locations...</Text>
                </View>
              ) : locationSuggestions.length > 0 || selectedLocation.trim() ? (
                <>
                  {/* Custom user input as first option */}
                  {selectedLocation.trim() && (
                    <TouchableOpacity
                      key="custom-location"
                      style={styles.modalSuggestionItem}
                      onPress={() =>
                        handleLocationSuggestionSelect({
                          display: selectedLocation.trim(),
                          fullAddress: selectedLocation.trim(),
                          lat: null,
                          lon: null,
                        })
                      }
                    >
                      <View style={styles.modalSuggestionIcon}>
                        <MapPinIcon
                          width={20}
                          height={20}
                          color={theme.colors.onSurfaceVariant}
                        />
                      </View>
                      <View style={styles.modalSuggestionContent}>
                        <Text style={styles.modalSuggestionName}>
                          "{selectedLocation.trim()}"
                        </Text>
                        <Text style={styles.modalSuggestionAddress}>
                          Use this location
                        </Text>
                      </View>
                    </TouchableOpacity>
                  )}
                  {/* API location suggestions */}
                  {locationSuggestions.map((suggestion, idx) => (
                    <TouchableOpacity
                      key={`${suggestion.display}-${idx}`}
                      style={styles.modalSuggestionItem}
                      onPress={() => handleLocationSuggestionSelect(suggestion)}
                    >
                      <View style={styles.modalSuggestionIcon}>
                        <MapPinIcon
                          width={20}
                          height={20}
                          color={theme.colors.onSurfaceVariant}
                        />
                      </View>
                      <View style={styles.modalSuggestionContent}>
                        <Text style={styles.modalSuggestionName}>
                          {suggestion.display}
                        </Text>
                        <Text style={styles.modalSuggestionAddress}>
                          {suggestion.fullAddress}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </>
              ) : selectedLocation && selectedLocation.trim().length >= 3 ? (
                <Text style={styles.noSuggestionsText}>
                  No locations found for "{selectedLocation}"
                </Text>
              ) : (
                <Text style={styles.noSuggestionsText}>
                  Start typing to search for locations
                </Text>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Search Results Modal */}
      <Modal
        visible={showResultsModal}
        animationType="slide"
        transparent={true}
        onRequestClose={closeResultsModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {searchResults.length} results found
              </Text>
              <TouchableOpacity
                onPress={closeResultsModal}
                style={styles.closeModalButton}
              >
                <CloseIcon
                  width={24}
                  height={24}
                  color={theme.colors.onSurface}
                />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalSuggestionsList}>
              {isSearchLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator
                    size="large"
                    color={theme.colors.primary}
                  />
                  <Text style={styles.loadingText}>Searching...</Text>
                </View>
              ) : searchResults.length > 0 ? (
                searchResults.map((business) => (
                  <TouchableOpacity
                    key={business.id}
                    style={styles.businessCard}
                    onPress={() => {
                      handleBusinessClick(business);
                      closeResultsModal();
                    }}
                  >
                    {/* Header: Name, Rating, Category */}
                    <View style={styles.cardHeader}>
                      <View style={styles.businessNameSection}>
                        <Text style={styles.businessName}>{business.name}</Text>
                        {business.type === 'queue' &&
                          business.parent_business_name &&
                          business.parent_business_name.trim() !==
                            business.name.trim() && (
                            <Text style={styles.parentBusinessName}>
                              at {business.parent_business_name}
                            </Text>
                          )}
                        {business.ratings && (
                          <View style={styles.ratingContainer}>
                            <StarIcon
                              width={14}
                              height={14}
                              color={theme.colors.onSurfaceVariant}
                            />
                            <Text style={styles.ratingsText}>
                              {business.ratings}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>

                    {/* Status Badge - Hide only for local businesses */}
                    {!(
                      business.type === 'business' && !business.fromInternet
                    ) &&
                      business.status && (
                        <View style={styles.statusSection}>
                          <Text
                            style={[
                              styles.statusText,
                              {
                                color:
                                  business.status === 'OPEN'
                                    ? theme.colors.primary
                                    : '#F44336',
                              },
                            ]}
                          >
                            {business.status === 'OPEN' ? 'OPEN' : 'CLOSED'}
                          </Text>
                        </View>
                      )}

                    {/* Address */}
                    <View style={styles.addressSection}>
                      <LocationIcon
                        width={14}
                        height={14}
                        color={theme.colors.onSurfaceVariant}
                      />
                      <View style={styles.addressContainer}>
                        <Text style={styles.addressText}>
                          {business.address}
                        </Text>
                      </View>
                    </View>

                    {/* Operating Hours */}
                    <View style={styles.operatingHoursSection}>
                      <Text style={styles.operatingHoursText}>
                        {(() => {
                          if (!business.operating_hours) {
                            return 'Operating Hours: Not available';
                          }

                          // Find today's operating hours or first open day
                          const today = new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                          });
                          const todayHours = business.operating_hours.find(
                            (h) => h.day === today
                          );
                          const openDay =
                            todayHours && !todayHours.is_closed
                              ? todayHours
                              : business.operating_hours.find(
                                  (h) => !h.is_closed
                                );

                          if (
                            openDay &&
                            openDay.shifts &&
                            openDay.shifts.length > 0
                          ) {
                            const shift = openDay.shifts[0];
                            const startTime = shift.start;
                            const endTime = shift.end;

                            // Convert 24-hour format to 12-hour AM/PM format
                            const formatTime = (time24) => {
                              const [hours, minutes] = time24.split(':');
                              const hour = parseInt(hours);
                              const ampm = hour >= 12 ? 'PM' : 'AM';
                              const hour12 =
                                hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                              return `${hour12}:${minutes} ${ampm}`;
                            };

                            const formattedStartTime = formatTime(startTime);
                            const formattedEndTime = formatTime(endTime);

                            return `Operating Hours: ${openDay.day} ${formattedStartTime} - ${formattedEndTime}`;
                          } else if (openDay) {
                            return `Operating Hours: ${openDay.day}`;
                          }

                          const hasShifts = business.operating_hours.some(
                            (h) => h.shifts && h.shifts.length > 0
                          );
                          if (hasShifts) {
                            return 'Operating Hours: Check schedule';
                          }
                          return 'Operating Hours: Closed';
                        })()}
                      </Text>
                    </View>

                    {/* Wait Time */}
                    {business.expectedWaitTime && (
                      <View style={styles.waitTimeSection}>
                        <Text style={styles.waitTimeText}>
                          Average Waiting Time: {business.expectedWaitTime} min
                        </Text>
                      </View>
                    )}

                    {/* Distance */}
                    {business.distance_km && (
                      <View style={styles.distanceSection}>
                        <Text style={styles.distanceText}>
                          Distance: {business.distance_km} km
                        </Text>
                      </View>
                    )}

                    {/* Footer: Platform & Directions */}
                    <View style={styles.cardFooter}>
                      <View style={styles.footerContent}>
                        <View style={styles.leftSection}>
                          {!business.fromInternet && (
                            <View style={styles.platformBadge}>
                              <Text style={styles.platformText}>
                                On Waitkyu
                              </Text>
                            </View>
                          )}
                        </View>
                        <View style={styles.rightSection}>
                          {business.map_location && (
                            <TouchableOpacity
                              style={styles.directionsButton}
                              onPress={() =>
                                openInMaps(
                                  business.map_location.latitude,
                                  business.map_location.longitude,
                                  business.name
                                )
                              }
                            >
                              <DirectionsIcon
                                width={16}
                                height={16}
                                color={theme.colors.onPrimary}
                              />
                              <Text style={styles.directionsText}>
                                Directions
                              </Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    </View>
                  </TouchableOpacity>
                ))
              ) : (
                <Text style={styles.noSuggestionsText}>
                  No businesses found for your search
                </Text>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default DiscoverSection;
